<template>
  <v-card
    class="open-tickets-summary-widget"
    :class="{ 'open-tickets-summary-widget--sidebar-open': isAnySidebarOpen }"
    elevation="0"
    rounded="lg"
  >
    <div v-if="widgetDataValue.loading" class="pa-6">
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" width="150" />
      <v-divider class="border-opacity-100 mb-6" color="indigo-faint" />
      <v-row>
        <v-col cols="3" class="d-flex justify-center align-center">
          <v-skeleton-loader type="avatar" size="180" class="bg-transparent" />
        </v-col>
        <v-col cols="9">
          <v-skeleton-loader
            v-for="i in 2"
            :key="i"
            type="list-item-two-line"
            class="bg-transparent mb-2"
          />
        </v-col>
      </v-row>
    </div>

    <!-- Data Display State -->
    <template v-else-if="widgetDataValue.data">
      <v-card-title class="d-flex align-center px-6 py-3">
        <div class="d-flex align-center">
          <span class="subtitle1">
            {{ $t(`dashboard.widgets.${widgetType}.title`) }}
          </span>
        </div>
        <v-spacer></v-spacer>
        <a class="body2 coro-link text-decoration-none" @click="navigateToTickets()">
          <div class="view-all-btn body2 coro-link item-clickable d-flex align-center">
            <span>{{ $t("general.viewAll") }}</span>
            <v-icon icon="$chevronRight" color="link" size="16" />
          </div>
        </a>
      </v-card-title>
      <v-divider class="border-opacity-100" color="indigo-faint" />

      <v-card-text class="pa-0">
        <div class="open-tickets-summary-widget__content">
          <div class="open-tickets-summary-widget__left-part py-6 px-6">
            <doughnut-chart
              :data="chartData"
              :options="chartOptions"
              @on-chart-click="handleChartClick"
            />
          </div>

          <v-divider
            class="open-tickets-summary-widget__divider"
            opacity="100"
            color="indigo-faint"
            vertical
          ></v-divider>

          <div class="open-tickets-summary-widget__right-part py-6 overflow-x-auto">
            <v-table class="bg-transparent elevation-0 rounded-0" density="compact" hover>
              <tbody>
                <tr v-for="item in remappedModules" :key="item.id">
                  <td class="text-left vulnerability-col pl-6 pr-0">
                    <div class="text-no-wrap d-flex align-center">
                      <div class="circle mr-2" :style="{ 'background-color': item.color }"></div>
                      <span
                        class="coro-link body2 text-primary text-ellipsis"
                        :title="item.name"
                        @click="navigateToTickets(item.id)"
                      >
                        {{ item.name }}
                      </span>
                    </div>
                  </td>
                  <td class="text-center number-col subtitle2 text-red-dark pa-0">
                    {{ item.count }}
                  </td>
                  <td class="text-right action-col pl-0 pr-6">
                    <v-btn
                      rounded
                      size="x-small"
                      color="primary"
                      @click="navigateToTickets(item.id)"
                    >
                      {{ $t("general.review") }}
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </div>
      </v-card-text>
    </template>

    <widget-error-state v-else :widget-type="widgetType" @retry-click="$emit('retry-fetch-data')" />
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import type { DataValue } from "@/types";
import DoughnutChart from "@/components/DoughnutChart.vue";
import { useTheme } from "vuetify";
import { CoroIcons } from "@/constants/coro-icon.ts";
import { useI18n } from "vue-i18n";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { TicketsModuleFilter, TicketStatus } from "@/constants/tickets.ts";
import { RouteName } from "@/constants/routes.ts";
import {
  SubscriptionModule,
  type TicketGeneratingSubscriptionModule,
} from "@/constants/workplaces.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import type { OpenTicketsSummaryWidgetData } from "@/_store/dashboard/dashboard.module.ts";
import { AdditionalWidgetType } from "@/constants/dashboard.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";

type ModuleViewModel = {
  id: TicketGeneratingSubscriptionModule;
  name: string;
  count: number;
  icon: string;
  color: string;
};

const MODULE_CONFIG = {
  [SubscriptionModule.CLOUD_SECURITY]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.CLOUD_SECURITY}`,
    icon: CoroIcons.CLOUD_XS,
    colorName: "labels-purple-full",
  },
  [SubscriptionModule.ENDPOINT_SECURITY]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.ENDPOINT_SECURITY}`,
    icon: CoroIcons.DEVICE_XS,
    colorName: "labels-green-full",
  },
  [SubscriptionModule.EMAIL_SECURITY]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.EMAIL_SECURITY}`,
    icon: CoroIcons.EMAIL,
    colorName: "labels-red-full",
  },
  [SubscriptionModule.USER_DATA_GOVERNANCE]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.USER_DATA_GOVERNANCE}`,
    icon: CoroIcons.USER_DATA_GOVERNANCE_SM,
    colorName: "labels-blue-full",
  },
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.ENDPOINT_DATA_GOVERNANCE}`,
    icon: CoroIcons.ENDPOINT_DATA_GOVERNANCE_S,
    colorName: "labels-yellow-full",
  },
  [SubscriptionModule.EDR]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.EDR}`,
    icon: CoroIcons.EDR_S,
    colorName: "labels-orange-full",
  },
} as Record<SubscriptionModule, { i18nKey: string; icon: string; colorName: string }>;

const MODULE_TO_FILTER_MAP = {
  [SubscriptionModule.CLOUD_SECURITY]: TicketsModuleFilter.CLOUD_SECURITY_WIDGET,
  [SubscriptionModule.EMAIL_SECURITY]: TicketsModuleFilter.EMAILS_WIDGET,
  [SubscriptionModule.ENDPOINT_SECURITY]: TicketsModuleFilter.DEVICE_WIDGET,
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: TicketsModuleFilter.ENDPOINT_DATA_GOVERNANCE,
  [SubscriptionModule.USER_DATA_GOVERNANCE]: TicketsModuleFilter.USER_DATA_GOVERNANCE,
  [SubscriptionModule.EDR]: TicketsModuleFilter.EDR,
} as Record<TicketGeneratingSubscriptionModule, TicketsModuleFilter>;

export default defineComponent({
  components: {
    WidgetErrorState,
    DoughnutChart,
  },
  props: {
    widgetDataValue: {
      type: Object as PropType<DataValue<OpenTicketsSummaryWidgetData>>,
      required: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props) {
    const widgetType = AdditionalWidgetType.OPEN_TICKETS_SUMMARY;
    const router = useRouter();
    const theme = useTheme();
    const i18n = useI18n();
    const filtersStore = useFiltersStore();
    const { isAnySidebarOpen } = storeToRefs(useSidebarPanelStore());

    const widgetPayload = computed(() => {
      // Provide a default structure to avoid errors and verbose checks
      return (
        props.widgetDataValue.data ?? {
          total: 0,
          delta: 0,
          modules: [],
        }
      );
    });

    const chartOptions = computed(() => {
      const delta = widgetPayload.value.delta;
      const prefix = delta > 0 ? "+" : "";
      // For open tickets, an increase is negative (bad), so we use a red color.
      const deltaColor =
        delta > 0
          ? theme.current.value.colors["red-dark"]
          : delta < 0
            ? theme.current.value.colors["green-dark"]
            : theme.current.value.colors["indigo-medium"];

      return {
        insideTextData: {
          title: widgetPayload.value.total,
          subtitle: i18n.t("general.openTickets"),
          thirdLine: {
            font: "400 14px Source Sans Pro",
            text: `${prefix}${delta}% ${i18n.t("coroImpact.fromLastMonth")}`,
            color: deltaColor,
          },
        },
      };
    });

    const remappedModules = computed((): ModuleViewModel[] => {
      return widgetPayload.value.modules
        .filter((m) => !!MODULE_CONFIG[m.name])
        .map((module) => {
          const config = MODULE_CONFIG[module.name];
          return {
            id: module.name,
            name: i18n.t(config.i18nKey),
            icon: config.icon,
            color: theme.current.value.colors[config.colorName],
            count: module.count,
          };
        })
        .slice()
        .sort((a, b) => b.count - a.count);
    });

    const chartData = computed(() => {
      if (!remappedModules.value.length) {
        return { labels: [], datasets: [] };
      }
      return {
        labels: remappedModules.value.map((item) => `${item.name} — ${item.count}`),
        datasets: [
          {
            backgroundColor: remappedModules.value.map((item) => item.color),
            hoverBackgroundColor: remappedModules.value.map((item) => item.color),
            data: remappedModules.value.map((item) => item.count),
            borderWidth: 0,
            cutout: "80%",
          },
        ],
      };
    });

    const navigateToTickets = (module?: TicketGeneratingSubscriptionModule) => {
      const route = RouteName.TICKETS;
      const context = FilterContext.TICKETS;
      const widget = module ? MODULE_TO_FILTER_MAP[module] : null;
      filtersStore.setFilters(context, {
        eventTimeRange: {
          start: "",
          end: "",
        },
        widget,
        status: TicketStatus.OPEN,
      });
      const gtmModuleName = module ? startCase(module) : "All";
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${GTMParams.SUMMARY_WIDGET} - ${gtmModuleName}`
      );
      return router.push({ name: route });
    };

    const handleChartClick = (index: number) => {
      const module = remappedModules.value[index]?.id;
      if (!module) {
        return;
      }
      navigateToTickets(module);
    };

    return {
      widgetType,
      remappedModules,
      navigateToTickets,
      chartData,
      chartOptions,
      handleChartClick,
      isAnySidebarOpen,
    };
  },
});
</script>

<style scoped lang="scss">
.open-tickets-summary-widget {
  &__content {
    display: flex;
  }
  &__left-part {
    flex: 0 0 35%;
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 275px;
    min-height: 250px;
  }
  &__right-part {
    flex: 1 1 65%;
    min-width: 0; // Prevents the flex item from overflowing its container
  }
}

// Stacked layout: chart on top, table below, vertical divider hidden.
@mixin stacked-layout {
  .open-tickets-summary-widget {
    &__content {
      flex-direction: column;
    }
    &__left-part {
      flex-basis: auto;
      width: 100%;
    }
    &__right-part {
      flex-basis: auto;
      width: 100%;
    }
    &__divider {
      display: none;
    }
  }
}

@media only screen and (max-width: 650px) {
  @include stacked-layout;
}

// When a sidebar (Setup Hub / AI Assistant) is open the dashboard content area shrinks;
// stack the layout regardless of viewport width, matching the other dashboard widgets.
.open-tickets-summary-widget--sidebar-open {
  @include stacked-layout;
}

.view-all-btn {
  user-select: none;

  &:hover span {
    text-decoration: underline;
  }
}

.action-col {
  width: 25%;
}

.number-col {
  width: 40px;
}

.vulnerability-col {
  max-width: 300px;
}

:deep(.v-table) {
  td {
    border: none !important;
    height: 44px;
  }

  .v-btn.v-btn--size-x-small {
    font-size: 12px;
  }
}
</style>
